import { getBearerToken } from "../../../api/postCall/bearerToken";
import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { logDataToConsole, logError } from "../../../utils/customLogger";
import type { SupportTicketApiAttachment } from "../supportTicketTypes";
import { parseSupportTicketApiError } from "../utils/parseSupportTicketApiError";

/**
 * Request body for POST /pharmacy/support-tickets/add-attachment.
 *
 * Registers a **ticket-scoped** attachment (no comment_id) — the file is
 * uploaded to Firebase Storage by the client; only its path/metadata is sent.
 */
export interface AddSupportTicketAttachmentParams {
  org_id: string;
  ticket_id: string;
  filename: string;
  gcs_path: string;
  content_type: string;
  size: number;
}

/**
 * Registers a Firebase Storage attachment against a ticket (not a comment).
 *
 * @param params - Organization id, ticket id, and uploaded file metadata.
 * @returns Stored attachment metadata from the API.
 */
export const addSupportTicketAttachment = async (
  params: AddSupportTicketAttachmentParams,
): Promise<SupportTicketApiAttachment> => {
  try {
    const token = await getBearerToken();
    const response = await fetch(API_ENDPOINTS.ADD_SUPPORT_TICKET_ATTACHMENT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error(
        await parseSupportTicketApiError(
          response,
          "Failed to upload attachment",
        ),
      );
    }

    const result = (await response.json()) as SupportTicketApiAttachment;
    logDataToConsole("addSupportTicketAttachment", result);
    return result;
  } catch (error) {
    logError(error as Error, "Error in addSupportTicketAttachment");
    throw error;
  }
};
